import { ApiService } from '../apiService'
import type {
  AdminEditSessionRequest,
  AdminEditStepEventRequest,
  AdminEditTrailRequest,
  AdminEditWalkRequest,
  AdminUserAchievementsDto,
  AdminUserActivitiesPageDto,
  AdminUserDataChangeResultDto,
  AdminUserReviewDto,
  AdminUserSessionDto,
  AdminUserStepEventsPageDto,
  AdminUserTrailsDto,
  AdminVoidSessionsRequest,
} from '../types/userData'

/**
 * The admin user data editor (Backlog 050) — AdminUserDataController, route `api/users/{userId}/data`.
 *
 * As in usersApi, the `/admin/` prefix is what sends a call to the Admin API (client.ts rewrites it
 * to admapi.hadv.quest/api/...), so these are written /admin/users/... for a route that is
 * /api/users/... on the server.
 *
 * Every removal is a POST to `.../delete`, `.../revoke` or `.../void` with a body rather than an
 * HTTP DELETE: each one carries a required reason, and DELETE bodies get dropped along the way.
 * Every mutation resolves to an AdminUserDataChangeResultDto describing what it moved.
 *
 * The paged reads take a 1-BASED `page`; DataTable is 0-based, so callers add one.
 */

type Change = Promise<AdminUserDataChangeResultDto>

const base = (userId: string) => `/admin/users/${userId}/data`

export const userDataApi = {
  // ---- Achievements and badges ----

  getAchievements: (userId: string) =>
    ApiService.get<AdminUserAchievementsDto>(`${base(userId)}/achievements`),

  revokeSystemAchievement: (userId: string, achievementId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/system-achievements/${achievementId}/revoke`, { reason }),

  restoreSystemAchievement: (userId: string, achievementId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/system-achievements/${achievementId}/restore`, { reason }),

  revokeCollectionAchievement: (userId: string, achievementId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/collection-achievements/${achievementId}/revoke`, { reason }),

  restoreCollectionAchievement: (userId: string, achievementId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/collection-achievements/${achievementId}/restore`, { reason }),

  /** Revoking a collection takes its completion and its badge. */
  revokeCollection: (userId: string, collectionId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/collections/${collectionId}/revoke`, { reason }),

  restoreCollection: (userId: string, collectionId: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/collections/${collectionId}/restore`, { reason }),

  // ---- Runs ----

  /** Newest first, voided runs included. */
  getSessions: (userId: string) => ApiService.get<AdminUserSessionDto[]>(`${base(userId)}/sessions`),

  voidSessions: (userId: string, request: AdminVoidSessionsRequest): Change =>
    ApiService.post(`${base(userId)}/sessions/void`, request),

  restoreSessions: (userId: string, request: AdminVoidSessionsRequest): Change =>
    ApiService.post(`${base(userId)}/sessions/restore`, request),

  editSession: (userId: string, sessionId: string, request: AdminEditSessionRequest): Change =>
    ApiService.put(`${base(userId)}/sessions/${sessionId}`, request),

  // ---- Steps ----

  getStepEvents: (userId: string, page: number, pageSize: number) =>
    ApiService.get<AdminUserStepEventsPageDto>(`${base(userId)}/step-events`, { page, pageSize }),

  editStepEvent: (userId: string, id: string, request: AdminEditStepEventRequest): Change =>
    ApiService.put(`${base(userId)}/step-events/${id}`, request),

  deleteStepEvent: (userId: string, id: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/step-events/${id}/delete`, { reason }),

  // ---- Trails and walks ----

  getTrails: (userId: string) => ApiService.get<AdminUserTrailsDto>(`${base(userId)}/trails`),

  editTrail: (userId: string, id: string, request: AdminEditTrailRequest): Change =>
    ApiService.put(`${base(userId)}/trails/${id}`, request),

  deleteTrail: (userId: string, id: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/trails/${id}/delete`, { reason }),

  editWalk: (userId: string, id: string, request: AdminEditWalkRequest): Change =>
    ApiService.put(`${base(userId)}/walks/${id}`, request),

  deleteWalk: (userId: string, id: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/walks/${id}/delete`, { reason }),

  // ---- Reviews and activity ----

  getReviews: (userId: string) => ApiService.get<AdminUserReviewDto[]>(`${base(userId)}/reviews`),

  deleteReview: (userId: string, id: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/reviews/${id}/delete`, { reason }),

  // Hide/unhide ANY rating (AdminRatingsController, `api/ratings`; Admin or Moderator). A hidden rating leaves every
  // public list and average; its author still sees it with the reason. Audited on the reviewer's history.

  hideRating: (ratingId: string, reason: string): Change =>
    ApiService.post(`/admin/ratings/${ratingId}/hide`, { reason }),

  unhideRating: (ratingId: string, reason?: string): Change =>
    ApiService.post(`/admin/ratings/${ratingId}/unhide`, { reason: reason || null }),

  getActivities: (userId: string, page: number, pageSize: number) =>
    ApiService.get<AdminUserActivitiesPageDto>(`${base(userId)}/activities`, { page, pageSize }),

  deleteActivity: (userId: string, id: string, reason: string): Change =>
    ApiService.post(`${base(userId)}/activities/${id}/delete`, { reason }),
}
